import { useState } from "react";
import { Plus, Trash2, Phone, Mail, Bell, BellOff, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  useTrustedCircle,
  TRUSTED_CIRCLE_MAX,
  type TrustedContact,
} from "@/lib/safety/useTrustedCircle";

const blank = {
  name: "",
  phone: "",
  email: "",
  relationship: "",
  notify_on_press: true,
  notify_on_missed_checkin: true,
};

const TrustedCirclePanel = () => {
  const { contacts, loading, add, update, remove } = useTrustedCircle();
  const [draft, setDraft] = useState(blank);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleAdd = async () => {
    if (!draft.name.trim()) {
      toast.error("Add a name.");
      return;
    }
    if (!draft.phone.trim() && !draft.email.trim()) {
      toast.error("Add at least a phone or email.");
      return;
    }
    setBusy(true);
    const { error } = await add({
      name: draft.name.trim(),
      phone: draft.phone.trim() || null,
      email: draft.email.trim() || null,
      relationship: draft.relationship.trim() || null,
      notify_on_press: draft.notify_on_press,
      notify_on_missed_checkin: draft.notify_on_missed_checkin,
    });
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    setDraft(blank);
    setAdding(false);
    toast.success("Contact added.");
  };

  const toggle = async (c: TrustedContact, field: "notify_on_press" | "notify_on_missed_checkin") => {
    const { error } = await update(c.id, { [field]: !c[field] });
    if (error) toast.error(error);
  };

  const handleRemove = async (id: string, name: string) => {
    if (!confirm(`Remove ${name} from your Trusted Circle?`)) return;
    const { error } = await remove(id);
    if (error) toast.error(error);
    else toast.success("Removed.");
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <Users className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="text-sm">
            Up to {TRUSTED_CIRCLE_MAX} people who get a silent ping when you press
            the Emergency Button or miss a Safety Check-in.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {contacts.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No contacts yet — add someone you trust.
          </p>
        )}
        {contacts.map((c) => (
          <div
            key={c.id}
            className="rounded-xl border border-border bg-card p-4 space-y-2"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{c.name}</p>
                {c.relationship && (
                  <p className="text-xs text-muted-foreground">{c.relationship}</p>
                )}
                <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {c.phone && (
                    <span className="inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </span>
                  )}
                  {c.email && (
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" /> {c.email}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRemove(c.id, c.name)}
                aria-label="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={() => toggle(c, "notify_on_press")}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                  c.notify_on_press
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground"
                }`}
              >
                {c.notify_on_press ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                Emergency press
              </button>
              <button
                type="button"
                onClick={() => toggle(c, "notify_on_missed_checkin")}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${
                  c.notify_on_missed_checkin
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground"
                }`}
              >
                {c.notify_on_missed_checkin ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
                Missed check-in
              </button>
            </div>
          </div>
        ))}
      </div>

      {adding ? (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="tc-name">Name</Label>
              <Input
                id="tc-name"
                value={draft.name}
                maxLength={100}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="tc-rel">Relationship</Label>
              <Input
                id="tc-rel"
                value={draft.relationship}
                maxLength={60}
                placeholder="e.g. Sister, friend, partner"
                onChange={(e) => setDraft({ ...draft, relationship: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="tc-phone">Phone</Label>
              <Input
                id="tc-phone"
                value={draft.phone}
                maxLength={30}
                inputMode="tel"
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="tc-email">Email</Label>
              <Input
                id="tc-email"
                value={draft.email}
                type="email"
                maxLength={200}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Notify on Emergency Button press</Label>
              <Switch
                checked={draft.notify_on_press}
                onCheckedChange={(v) => setDraft({ ...draft, notify_on_press: v })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Notify on missed Safety Check-in</Label>
              <Switch
                checked={draft.notify_on_missed_checkin}
                onCheckedChange={(v) => setDraft({ ...draft, notify_on_missed_checkin: v })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => { setAdding(false); setDraft(blank); }}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={busy}>
              {busy ? "Saving…" : "Save contact"}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setAdding(true)}
          disabled={contacts.length >= TRUSTED_CIRCLE_MAX}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add contact ({contacts.length}/{TRUSTED_CIRCLE_MAX})
        </Button>
      )}
    </div>
  );
};

export default TrustedCirclePanel;
